import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Building2, Plus, Loader2, Pencil, Trash2, ChevronDown, ChevronRight,
    CalendarDays, DollarSign, User as UserIcon, ShieldCheck,
} from 'lucide-react';
import StructureService from '../../services/StructureService';
import { Card, Button, toast, ConfirmationModal, ErrorState } from './V2Components';
import StructureForm from './Structures/StructureForm';
import StructureEventForm from './Structures/StructureEventForm';
import StructureEventFiles from './Structures/StructureEventFiles';

/**
 * House "Structures" tab. Lazy-fetches the house's structures (roof / foundation
 * / siding), each with its dated events and files embedded. Structures and
 * events are created/edited through inline in-page forms (no overlay, no
 * drill-in routes) — mirrors the Electrical tab.
 */

const TYPE_STYLES = {
    Roof: 'bg-emerald-500/10 text-emerald-600',
    Foundation: 'bg-amber-500/10 text-amber-600',
    Siding: 'bg-sky-500/10 text-sky-600',
};

const formatDate = (iso) => {
    if (!iso) return null;
    // iso is a plain YYYY-MM-DD date string; parse as local to avoid an
    // off-by-one from UTC interpretation.
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatCost = (cost) => {
    if (cost == null) return null;
    return `$${Number(cost).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const StructuresTab = ({ houseId }) => {
    const [structures, setStructures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [loadError, setLoadError] = useState(null);
    // Which structure's events section is expanded.
    const [expandedId, setExpandedId] = useState(null);

    // Structure form state.
    const [showStructureForm, setShowStructureForm] = useState(false);
    const [editingStructure, setEditingStructure] = useState(null);
    const [structureSaving, setStructureSaving] = useState(false);
    const [structureFieldErrors, setStructureFieldErrors] = useState([]);

    // Event form state — the event form belongs to the structure whose id is
    // `eventFormStructureId`.
    const [eventFormStructureId, setEventFormStructureId] = useState(null);
    const [editingEvent, setEditingEvent] = useState(null);
    const [eventSaving, setEventSaving] = useState(false);
    const [eventFieldErrors, setEventFieldErrors] = useState([]);

    // Delete-confirmation state.
    const [structureToDelete, setStructureToDelete] = useState(null);
    const [deletingStructure, setDeletingStructure] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);
    const [deletingEvent, setDeletingEvent] = useState(false);

    // Monotonic request token: each fetch captures the current value and only
    // applies its result if it's still the latest. Guards against a slow
    // prior-house response resolving after the user switched houses and
    // overwriting the new house's data.
    const fetchIdRef = useRef(0);

    const fetchStructures = useCallback(async ({ isInitial = false } = {}) => {
        const requestId = ++fetchIdRef.current;
        try {
            if (isInitial) setLoading(true);
            else setRefreshing(true);
            const data = await StructureService.getStructures(houseId);
            if (requestId !== fetchIdRef.current) return; // superseded by a newer fetch
            setStructures(data.structures || []);
            setLoadError(null);
        } catch (error) {
            if (requestId !== fetchIdRef.current) return;
            setLoadError(error.message || 'Failed to load structures');
            toast.error?.(error.message || 'Failed to load structures');
        } finally {
            if (requestId === fetchIdRef.current) {
                if (isInitial) setLoading(false);
                else setRefreshing(false);
            }
        }
    }, [houseId]);

    useEffect(() => {
        // Clear all house-scoped state when the house changes so the previous
        // house's structures / open forms / edit-delete targets can't linger or
        // act on the wrong house while the new fetch is in flight.
        setStructures([]);
        setExpandedId(null);
        setLoadError(null);
        setShowStructureForm(false);
        setEditingStructure(null);
        setStructureFieldErrors([]);
        setEventFormStructureId(null);
        setEditingEvent(null);
        setEventFieldErrors([]);
        setStructureToDelete(null);
        setEventToDelete(null);
        if (houseId) fetchStructures({ isInitial: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [houseId]);

    const structureFormOpen = showStructureForm;
    const eventFormOpen = eventFormStructureId !== null;
    // Any inline form open — suppress competing affordances.
    const formOpen = structureFormOpen || eventFormOpen;

    // ---------------------------- Structure handlers ---------------------------

    const openAddStructure = () => {
        setEditingStructure(null);
        setStructureFieldErrors([]);
        setShowStructureForm(true);
    };

    const handleSaveStructure = async (payload) => {
        setStructureSaving(true);
        setStructureFieldErrors([]);
        try {
            if (editingStructure) {
                await StructureService.updateStructure(editingStructure.id, payload);
                toast.success('Structure updated');
            } else {
                const created = await StructureService.createStructure(houseId, payload);
                toast.success('Structure added');
                if (created?.id) setExpandedId(created.id);
            }
            setShowStructureForm(false);
            setEditingStructure(null);
            await fetchStructures();
        } catch (error) {
            if (error.errors?.length) setStructureFieldErrors(error.errors);
            else toast.error?.(error.message || 'Failed to save structure');
        } finally {
            setStructureSaving(false);
        }
    };

    const handleDeleteStructure = async () => {
        if (!structureToDelete) return;
        setDeletingStructure(true);
        try {
            await StructureService.deleteStructure(structureToDelete.id);
            toast.success('Structure deleted');
            setStructureToDelete(null);
            await fetchStructures();
        } catch (error) {
            toast.error?.(error.message || 'Failed to delete structure');
        } finally {
            setDeletingStructure(false);
        }
    };

    // ------------------------------ Event handlers -----------------------------

    const openAddEvent = (structureId) => {
        setEditingEvent(null);
        setEventFieldErrors([]);
        setEventFormStructureId(structureId);
    };

    const openEditEvent = (structureId, event) => {
        setEditingEvent(event);
        setEventFieldErrors([]);
        setEventFormStructureId(structureId);
    };

    const closeEventForm = () => {
        setEventFormStructureId(null);
        setEditingEvent(null);
        setEventFieldErrors([]);
    };

    const handleSaveEvent = async (payload) => {
        if (eventFormStructureId == null) return;
        setEventSaving(true);
        setEventFieldErrors([]);
        try {
            if (editingEvent) {
                await StructureService.updateEvent(editingEvent.id, payload);
                toast.success('Event updated');
            } else {
                await StructureService.createEvent(eventFormStructureId, payload);
                toast.success('Event added');
            }
            closeEventForm();
            await fetchStructures();
        } catch (error) {
            if (error.errors?.length) setEventFieldErrors(error.errors);
            else toast.error?.(error.message || 'Failed to save event');
        } finally {
            setEventSaving(false);
        }
    };

    const handleDeleteEvent = async () => {
        if (!eventToDelete) return;
        setDeletingEvent(true);
        try {
            await StructureService.deleteEvent(eventToDelete.id);
            toast.success('Event deleted');
            setEventToDelete(null);
            closeEventForm();
            await fetchStructures();
        } catch (error) {
            toast.error?.(error.message || 'Failed to delete event');
        } finally {
            setDeletingEvent(false);
        }
    };

    // --------------------------------- Render ----------------------------------

    if (loading) {
        return (
            <div className="flex justify-center p-12">
                <Loader2 className="animate-spin text-[var(--primary)]" size={32} />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-bold text-[var(--text-main)] flex items-center gap-2">
                        Structures
                        {refreshing && (
                            <Loader2 className="animate-spin text-[var(--text-muted)]" size={14} aria-label="Refreshing" />
                        )}
                    </h3>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                        Track your roof, foundation, and siding — with dated repairs, photos, and invoices.
                    </p>
                </div>
                {structures.length > 0 && !formOpen && (
                    <Button leadingIcon={Plus} onClick={openAddStructure}>
                        Add Structure
                    </Button>
                )}
            </div>

            {/* Inline Add/Edit Structure form. */}
            <StructureForm
                isOpen={showStructureForm}
                onClose={() => {
                    setShowStructureForm(false);
                    setEditingStructure(null);
                    setStructureFieldErrors([]);
                }}
                onSave={handleSaveStructure}
                structure={editingStructure}
                saving={structureSaving}
                fieldErrors={structureFieldErrors}
            />

            {loadError && structures.length === 0 ? (
                <Card>
                    <ErrorState
                        title="Couldn't load structures"
                        message={loadError}
                        onRetry={() => fetchStructures({ isInitial: true })}
                    />
                </Card>
            ) : structures.length === 0 ? (
                !showStructureForm && (
                    <Card className="p-10 text-center">
                        <Building2 size={48} className="mx-auto mb-3 text-[var(--text-muted)] opacity-50" />
                        <p className="text-[var(--text-muted)] mb-4">No structures tracked yet.</p>
                        <Button leadingIcon={Plus} onClick={openAddStructure}>
                            Add your first structure
                        </Button>
                    </Card>
                )
            ) : (
                <div className="space-y-4">
                    {structures.map((structure) => {
                        const isExpanded = expandedId === structure.id;
                        const thisEventFormOpen = eventFormStructureId === structure.id;
                        return (
                            <Card key={structure.id} className="p-4 sm:p-5">
                                {/* Structure header */}
                                <div className="flex items-start justify-between gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setExpandedId(isExpanded ? null : structure.id)}
                                        className="flex items-start gap-3 text-left flex-1 min-w-0"
                                        aria-expanded={isExpanded}
                                        aria-label={`Toggle ${structure.name || structure.structure_type} details`}
                                    >
                                        <span className="mt-0.5 text-[var(--text-muted)]">
                                            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                        </span>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span
                                                    className={`px-2 py-0.5 rounded-full text-xs font-bold ${TYPE_STYLES[structure.structure_type] || 'bg-[var(--bg-hover)] text-[var(--text-secondary)]'}`}
                                                >
                                                    {structure.structure_type}
                                                </span>
                                                {structure.name && (
                                                    <span className="font-bold text-[var(--text-main)] truncate">
                                                        {structure.name}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-[var(--text-secondary)]">
                                                {structure.material && <span>{structure.material}</span>}
                                                {structure.install_date && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <CalendarDays size={12} /> Installed {formatDate(structure.install_date)}
                                                    </span>
                                                )}
                                                {structure.warranty_expiration && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <ShieldCheck size={12} /> Warranty to {formatDate(structure.warranty_expiration)}
                                                    </span>
                                                )}
                                                <span>{structure.event_count} event{structure.event_count === 1 ? '' : 's'}</span>
                                            </div>
                                        </div>
                                    </button>
                                    {!formOpen && (
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button
                                                type="button"
                                                aria-label="Edit structure"
                                                onClick={() => {
                                                    setEditingStructure(structure);
                                                    setStructureFieldErrors([]);
                                                    setShowStructureForm(true);
                                                }}
                                                className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-hover)] transition"
                                            >
                                                <Pencil size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                aria-label="Delete structure"
                                                onClick={() => setStructureToDelete(structure)}
                                                className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--error)] hover:bg-[var(--bg-hover)] transition"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {structure.notes && isExpanded && (
                                    <p className="mt-3 text-sm text-[var(--text-secondary)] whitespace-pre-wrap">
                                        {structure.notes}
                                    </p>
                                )}

                                {/* Events section (expanded) */}
                                {isExpanded && (
                                    <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h5 className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide">
                                                Event history
                                            </h5>
                                            {!formOpen && (
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    leadingIcon={Plus}
                                                    onClick={() => openAddEvent(structure.id)}
                                                >
                                                    Add Event
                                                </Button>
                                            )}
                                        </div>

                                        {/* Inline Add/Edit Event form (this structure only). */}
                                        {thisEventFormOpen && (
                                            <StructureEventForm
                                                isOpen={thisEventFormOpen}
                                                onClose={closeEventForm}
                                                onSave={handleSaveEvent}
                                                onDelete={() => setEventToDelete(editingEvent)}
                                                event={editingEvent}
                                                saving={eventSaving}
                                                fieldErrors={eventFieldErrors}
                                            />
                                        )}

                                        {(structure.events || []).length === 0 && !thisEventFormOpen ? (
                                            <p className="text-sm text-[var(--text-muted)]">
                                                No events yet. Add an installation, repair, or inspection.
                                            </p>
                                        ) : (
                                            <div className="space-y-3">
                                                {(structure.events || []).map((event) => (
                                                    <Card key={event.id} className="p-3 sm:p-4 bg-[var(--bg-hover)]">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="font-semibold text-[var(--text-main)]">
                                                                        {event.event_type}
                                                                    </span>
                                                                    <span className="inline-flex items-center gap-1 text-xs text-[var(--text-secondary)]">
                                                                        <CalendarDays size={12} /> {formatDate(event.event_date)}
                                                                    </span>
                                                                </div>
                                                                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-[var(--text-secondary)]">
                                                                    {event.cost != null && (
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <DollarSign size={12} /> {formatCost(event.cost)}
                                                                        </span>
                                                                    )}
                                                                    {event.contractor && (
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <UserIcon size={12} /> {event.contractor}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {!formOpen && (
                                                                <button
                                                                    type="button"
                                                                    aria-label="Edit event"
                                                                    onClick={() => openEditEvent(structure.id, event)}
                                                                    className="p-2 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-card)] transition shrink-0"
                                                                >
                                                                    <Pencil size={14} />
                                                                </button>
                                                            )}
                                                        </div>

                                                        {event.notes && (
                                                            <p className="mt-2 text-sm text-[var(--text-secondary)] whitespace-pre-wrap">
                                                                {event.notes}
                                                            </p>
                                                        )}

                                                        <div className="mt-3">
                                                            <StructureEventFiles event={event} onChange={fetchStructures} />
                                                        </div>
                                                    </Card>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}

            <ConfirmationModal
                isOpen={!!structureToDelete}
                onClose={() => setStructureToDelete(null)}
                onConfirm={handleDeleteStructure}
                title="Delete structure?"
                message={
                    structureToDelete
                        ? `"${structureToDelete.name || structureToDelete.structure_type}" and all its events, photos, and documents will be permanently deleted.`
                        : ''
                }
                confirmText="Delete"
                isDanger
                isLoading={deletingStructure}
            />

            <ConfirmationModal
                isOpen={!!eventToDelete}
                onClose={() => setEventToDelete(null)}
                onConfirm={handleDeleteEvent}
                title="Delete event?"
                message={
                    eventToDelete
                        ? `This ${eventToDelete.event_type} event and its photos/documents will be permanently deleted.`
                        : ''
                }
                confirmText="Delete"
                isDanger
                isLoading={deletingEvent}
            />
        </div>
    );
};

export default StructuresTab;
